import React, { useEffect, useRef, useState } from 'react';
import './index.scss';
import moment from 'moment';
import {
  getEventName,
  getEventTypeTagClassName,
} from '@op/pages/Overview/KeyMetrics/utils';
import { getLocale } from 'umi';
import { comparableTime } from '@op/utils/time';
import { ParamsTimeType } from '@op/types/common';
import { getRealXAxisValue } from '../../../utils';
import CommonBubble from '@home/components/CommonBubble';
import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
const seriesName = 'TopTipsEvent';

export default function EventItem({
  myChart,
  xAxisValue,
  eventList,
  hide = true,
  format,
  backPositionInfo,
  halfBarWidth,
  maxTimeValue,
  showEventAxis,
  dateType,
  xAxisData,
  yAxisMin = false,
  eventDetailsNav,
}: {
  myChart: any;
  xAxisValue: string;
  eventList: Array<any>;
  hide?: boolean;
  format: string;
  halfBarWidth: number;
  maxTimeValue: string;
  showEventAxis: boolean;
  dateType: ParamsTimeType;
  xAxisData: Array<string>;
  yAxisMin?: boolean;
  eventDetailsNav?: {
    minCountExclusive: number;
    onNavigate: (item: any, xAxisValue: string) => void;
    /** 存在时优先于 minCountExclusive + eventList.length（如经分合并展示后条数变少） */
    shouldAllowDetailsNav?: (xAxisValue: string) => boolean;
  };
  backPositionInfo: (item: {
    eventLine: any;
    barLeftPosition: number;
    barBottomPosition: number;
    eventBox: any;
  }) => void;
}) {
  const lang = getLocale();

  const eventBoxRef = useRef<any>(null);
  const eventLineRef = useRef<any>(null);
  const currentItemPosition = useRef<number>(0);
  function getBarLeftPosition({
    seriesName,
    xAxis = xAxisValue,
  }: {
    seriesName: string;
    xAxis?: string;
  }) {
    const realXAxisValue = getRealXAxisValue({
      xAxis,
      dateType,
      format,
      xAxisData,
    });

    try {
      // 获取 Y 轴的配置
      const option = myChart.getOption();
      const yAxis = option?.yAxis?.[0];

      // 如果设置了 yAxisMin，使用图表底部对应的 Y 值
      // 否则使用 0
      let yValue = 0;
      if (yAxisMin && yAxis) {
        // 获取 Y 轴的实际范围
        const yAxisModel = myChart.getModel().getComponent('yAxis', 0);
        if (yAxisModel) {
          const scale = yAxisModel.axis.scale;
          const extent = scale.getExtent();
          // 使用 Y 轴的最小值
          yValue = extent[0];
        }
      }

      // 获取柱子底部的像素坐标
      const bottomPixelPosition = myChart.convertToPixel(
        { seriesName: seriesName },
        [realXAxisValue, yValue],
      );

      return { left: bottomPixelPosition[0], bottom: bottomPixelPosition[1] };
    } catch (error) {
      return { left: 0, bottom: 0 };
    }
  }

  function setEventBoxPosition({ seriesName }: { seriesName: string; }) {
    // 获取柱子底部的像素坐标
    const leftPosition = getBarLeftPosition({ seriesName });
    currentItemPosition.current = leftPosition.left;
    if (eventBoxRef.current) {
      backPositionInfo({
        barLeftPosition: leftPosition.left,
        barBottomPosition: leftPosition.bottom,
        eventBox: eventBoxRef.current,
        eventLine: eventLineRef.current,
      });
    }
  }

  useEffect(() => {
    if (myChart) {
      setEventBoxPosition({ seriesName });
    }
  }, [myChart]);

  const [renderMarkArea, setRenderMarkArea] = useState({
    width: '0px',
    left: '0px',
  });
  const [eventLineShow, setEventLineShow] = useState(false);
  const [markAreaShow, setMarkAreaShow] = useState(false);
  const [markAreaTimes, setMarkAreaTimes] = useState<string>('');

  const handleMouseEnter = (item: any) => {
    // 没传时间就就不展示标域
    if (!item.start_time && !item.end_time) {
      return;
    }
    const endTime = moment(item.end_time).format(format);
    const startTime = moment(item.start_time).format(format);
    // 大于最大轴
    const maxThanMaxTime = comparableTime(maxTimeValue, endTime);
    const realXAxis = maxThanMaxTime ? maxTimeValue : endTime;

    const left = currentItemPosition.current - halfBarWidth;

    const endItem = getBarLeftPosition({ seriesName, xAxis: realXAxis });
    const endLeftValue = endItem.left + halfBarWidth;
    const width = endLeftValue - left;

    setMarkAreaTimes(startTime);
    setRenderMarkArea({ left: `${left}px`, width: `${width}px` });
    setMarkAreaShow(true);
  };

  const handleMouseLeave = () => {
    setMarkAreaShow(false);
    setEventLineShow(false);
    setMarkAreaTimes('');
    setRenderMarkArea({ left: '0', width: '0' });
  };

  // 根据当前事件在 xAxisData 中的位置计算 bubble 的 placement
  const getBubblePlacement = (): 'top' | 'left' | 'right' => {
    // 使用 getRealXAxisValue 转换 xAxisValue 格式，使其与 xAxisData 中的格式一致
    const realXAxisValue = getRealXAxisValue({
      xAxis: xAxisValue,
      dateType,
      format,
      xAxisData,
    });

    const currentIndex = xAxisData?.findIndex(
      (item) => item === realXAxisValue,
    );
    if (currentIndex === -1) return 'top';

    const totalLength = xAxisData?.length;
    const position = currentIndex / totalLength;

    // 前 10% 显示在右侧
    if (position <= 0.1) {
      return 'right';
    }
    // 后 10% 显示在左侧
    if (position >= 0.9) {
      return 'left';
    }
    // 其他情况显示在顶部
    return 'top';
  };

  const detailsNavActive = Boolean(
    eventDetailsNav &&
      (typeof eventDetailsNav.shouldAllowDetailsNav === 'function'
        ? eventDetailsNav.shouldAllowDetailsNav(xAxisValue)
        : Array.isArray(eventList) &&
          eventList.length > (eventDetailsNav.minCountExclusive ?? 3)),
  );

  return (
    <>
      <div
        className={`dbo-mark-area-box ${!showEventAxis ? '--long' : ''} ${
          markAreaShow ? '--show' : '--hide'
        }`}
        style={renderMarkArea}
      >
        <div
          className="dbo-mark-area-time"
          style={{ left: `${halfBarWidth}px` }}
        >
          <p className="dbo-mark-time-item">{markAreaTimes}</p>
        </div>
      </div>
      <div
        ref={eventBoxRef}
        className={`dbo-event-item-wrapper ${hide ? '--hide' : ''}`}
      >
        <ul
          className="dbo-event-box"
          onMouseLeave={() => handleMouseLeave()}
          onMouseEnter={() => setEventLineShow(true)}
        >
          {eventList.map((item, idx) => {
            // 不可用 key={item}：item 为对象时 React 会当成 "[object Object]"，全部重复
            const eventRowKey = `${xAxisValue}-${idx}-${
              item?.id ?? item?.event_id ?? ''
            }-${item?.start_time ?? ''}-${item?.event_title_en ?? item?.name ?? ''}`;
            const barColor =
              typeof item?.event_bar_color === 'string'
                ? item.event_bar_color.trim()
                : '';
            const barCustomClass = barColor ? ' dbo-event-item--bar-custom' : '';
            return (
              <CommonBubble
                key={eventRowKey}
                content={getName(
                  lang === LANGUAGE_EN,
                  item?.description,
                  item?.description_cn,
                )}
                placement={getBubblePlacement()}
                dark={true}
              >
                <li
                  className={`dbo-event-item ${getEventTypeTagClassName(
                    item.event_type,
                  )}${barCustomClass}${
                    detailsNavActive ? ' dbo-event-item--details-nav' : ''
                  }`}
                  style={
                    barColor
                      ? ({
                          ['--dbo-event-bar-bg' as string]: barColor,
                        } as React.CSSProperties)
                      : undefined
                  }
                  onMouseEnter={() => handleMouseEnter(item)}
                  onClick={(e) => {
                    if (!detailsNavActive || !eventDetailsNav) {
                      return;
                    }
                    e.stopPropagation();
                    eventDetailsNav.onNavigate(item, xAxisValue);
                  }}
                  role={detailsNavActive ? 'button' : undefined}
                  tabIndex={detailsNavActive ? 0 : undefined}
                  onKeyDown={
                    detailsNavActive
                      ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          eventDetailsNav?.onNavigate(item, xAxisValue);
                        }
                      }
                      : undefined
                  }
                >
                  <span className="dbo-event-item-text">
                    {getEventName(item, lang)}
                  </span>
                </li>
              </CommonBubble>
            );
          })}
        </ul>
      </div>
      <div
        ref={eventLineRef}
        className={`dbo-event-box-line ${eventLineShow ? '--highlight' : ''}`}
      ></div>
    </>
  );
}
